import type { SearchResponse } from '@algolia/client-search';
import type { FC, PropsWithChildren } from 'react';

import { useMemo } from 'react';

import type { TileProps } from '~/components/shared/recommendations/tile/Tile';
import { TileProvider } from '~/components/shared/recommendations/tile/TileContext';
import type { ProductHit } from '~/types/algolia';
import { useAppContext } from '~/utilities/context/static/AppContext';
import type { StyleVariant } from '~/utilities/graphql/codegen';
import type { RecentlyViewedProduct } from '~/utilities/helpers';
import { mapProductToAlgolia, mapVariantsToAlgolia } from '~/utilities/helpers/mapToAlgolia';

export const SolrTileProvider: FC<PropsWithChildren<TileProps>> = ({
  product,
  variants,
  children,
  ...restProps
}) => {
  const { lang } = useAppContext();

  const transformedProduct = useMemo(
    () => mapProductToAlgolia(product as RecentlyViewedProduct, lang),
    [product, lang]
  );

  const transformedVariants = useMemo(
    () => (variants as StyleVariant[])?.map(variant => mapVariantsToAlgolia(variant)),
    [variants]
  ) as SearchResponse<ProductHit>['hits'];

  return (
    <TileProvider
      value={{
        product: transformedProduct,
        variants: transformedVariants,
        ...restProps,
      }}
    >
      {children}
    </TileProvider>
  );
};
